/**
 * Activity on the timeline: a run's bar as bursts of work with idle gaps between them, the
 * marks where it was resumed, compacted or reset, summaries per workflow and per session, and
 * agents that look stuck. Pure functions over what the machines report (module timeline).
 */
import { ACTIVITY_IDLE_GAP_MS, ACTIVITY_LOOKBACK_MS, AGENT_STUCK_BUSY_MS, AGENT_STUCK_MS, machineLiveness } from '@orgus/protocol';
import type { Activity, ActivityMark, Machine, Session, Ts } from '@orgus/protocol';
import { runsOf, unitsOf } from './model.ts';
import type { TimelineRun } from './model.ts';

export interface BarPiece {
  from: Ts;
  to: Ts;
  /** active: at work. idle: between bursts. quiet: marked running, but nothing since the last entry. */
  kind: 'active' | 'idle' | 'quiet';
}

type Timed = Pick<TimelineRun, 'status' | 'startedAt' | 'endedAt' | 'activity'>;

/**
 * Why a run has no activity at all: machines read transcripts for bursts only for agents that
 * worked within ACTIVITY_LOOKBACK_MS, so an older one has none on any agent version.
 */
export function noActivityReason(r: Pick<TimelineRun, 'status' | 'startedAt' | 'endedAt'>, now: Ts): 'old' | 'unreported' {
  return r.status !== 'running' && (r.endedAt ?? r.startedAt) < now - ACTIVITY_LOOKBACK_MS ? 'old' : 'unreported';
}

/** Activity complete enough to draw (one still being read draws as a plain bar). */
export function usableActivity(r: Pick<TimelineRun, 'activity'>): Activity | null {
  const a = r.activity;
  return a && !a.partial && a.spans.length ? a : null;
}

/** Whether a running run counts as at work right now: heard from moments ago, or mid-turn and not stuck. */
export function liveNow(a: Activity, now: Ts): boolean {
  const quiet = now - a.lastAt;
  return quiet < ACTIVITY_IDLE_GAP_MS || (a.busy && quiet <= AGENT_STUCK_BUSY_MS);
}

/**
 * The pieces of a run's bar, oldest first: its bursts, the idle gaps between them and, for a
 * running run that went quiet, the quiet stretch up to now. A run with no activity reported
 * is one active piece from start to end, as before.
 */
export function barPieces(r: Timed, now: Ts): BarPiece[] {
  const a = usableActivity(r);
  if (!a) return [{ from: r.startedAt, to: r.endedAt ?? now, kind: 'active' }];
  const out: BarPiece[] = [];
  a.spans.forEach((s, i) => {
    const prev = a.spans[i - 1];
    if (prev && s.from > prev.to) out.push({ from: prev.to, to: s.from, kind: 'idle' });
    out.push({ from: s.from, to: Math.max(s.from, s.to), kind: 'active' });
  });
  const last = out[out.length - 1] as BarPiece;
  if (r.status === 'running') {
    if (liveNow(a, now)) last.to = Math.max(last.to, now);
    else if (now > last.to) out.push({ from: last.to, to: now, kind: 'quiet' });
  }
  return out;
}

/** A bar piece placed inside the bar's visible extent: left and right edges as fractions of it. */
export interface PieceBox {
  kind: BarPiece['kind'];
  l: number;
  r: number;
}

/** Where a time sits inside [from, to], as a fraction. */
const frac = (t: Ts, from: Ts, to: Ts) => Math.max(0, Math.min(1, (t - from) / Math.max(1, to - from)));

/**
 * The pieces of a bar inside its visible extent [from, to] (the window clips a bar's start).
 * A piece wholly outside it is left out: a burst that ended before the window opened is not
 * drawn, where clamping would leave a sliver at the window's edge that reads as work.
 */
export function pieceBoxes(pieces: readonly BarPiece[], from: Ts, to: Ts): PieceBox[] {
  const out: PieceBox[] = [];
  for (const p of pieces) {
    if (p.to < from || p.from > to || (p.to === from && p.from < from)) continue;
    const l = frac(p.from, from, to);
    const r = frac(p.to, from, to);
    if (r <= l && p.kind !== 'active') continue;
    out.push({ kind: p.kind, l, r });
  }
  return out;
}

/** Width of a mark's glyph on a bar, in px, plus a pixel of air. */
export const MARK_GLYPH_PX = 15;

/**
 * Marks placed on a bar `px` wide spanning [from, to], as fractions of it, oldest first. A
 * mark that would cover the one before it (a compaction minutes after a resume, on a 24 h
 * window) moves right just enough to sit beside it, so every glyph stays visible; the tooltip
 * and the drawer keep the exact times.
 */
export function markSlots(marks: readonly ActivityMark[], from: Ts, to: Ts, px: number): { mark: ActivityMark; x: number }[] {
  const sorted = [...marks].sort((a, b) => a.at - b.at);
  const step = px > 0 ? MARK_GLYPH_PX / px : 0;
  let prev = -Infinity;
  return sorted.map((mark) => {
    const x = Math.max(frac(mark.at, from, to), prev + step);
    prev = x;
    return { mark, x };
  });
}

/** Time at work: the bursts (plus the live stretch of a busy run), or start to end without activity. */
export function activeMs(r: Timed, now: Ts): { ms: number; measured: boolean } {
  const a = usableActivity(r);
  if (!a) return { ms: Math.max(0, (r.endedAt ?? now) - r.startedAt), measured: false };
  return { ms: barPieces(r, now).reduce((n, p) => n + (p.kind === 'active' ? p.to - p.from : 0), 0), measured: true };
}

/** The bursts of a bar (active pieces). */
export function bursts(r: Timed, now: Ts): BarPiece[] {
  return barPieces(r, now).filter((p) => p.kind === 'active');
}

/** Marks of a run inside [start, end]. */
export function marksIn(r: Pick<TimelineRun, 'activity'>, start: Ts, end: Ts): ActivityMark[] {
  return (usableActivity(r)?.marks ?? []).filter((m) => m.at >= start && m.at <= end);
}

/**
 * A workflow's activity: the union of its agents' bursts (an agent with none reported counts
 * from its start to its end). Null when no agent reports activity.
 */
export function unionActivity(agents: readonly Timed[], now: Ts): Activity | null {
  if (!agents.some((x) => usableActivity(x))) return null;
  const all: { from: Ts; to: Ts }[] = [];
  let busy = false;
  let lastAt = 0;
  for (const x of agents) {
    for (const p of barPieces(x, now)) if (p.kind === 'active') all.push({ from: p.from, to: p.to });
    const a = usableActivity(x);
    if (x.status === 'running' && (!a || liveNow(a, now))) busy = true;
    lastAt = Math.max(lastAt, a?.lastAt ?? x.endedAt ?? x.startedAt);
  }
  all.sort((p, q) => p.from - q.from);
  const spans: { from: Ts; to: Ts }[] = [];
  for (const s of all) {
    const top = spans[spans.length - 1];
    if (top && s.from <= top.to + ACTIVITY_IDLE_GAP_MS) top.to = Math.max(top.to, s.to);
    else spans.push({ ...s });
  }
  return { spans, marks: [], activeMs: spans.reduce((n, s) => n + s.to - s.from, 0), lastAt, busy };
}

// ---------------------------------------------------------------------------
// Summaries

export interface RunSummary {
  total: number;
  running: number;
  done: number;
  failed: number;
  stopped: number;
  /** Agents or subagents finished (done, failed or stopped). */
  finished: number;
  toolCalls: number;
  /** The run at work the longest (active time when reported, else start to end). */
  longest: { run: TimelineRun; ms: number; measured: boolean } | null;
}

export function summarize(units: readonly TimelineRun[], now: Ts): RunSummary {
  const s: RunSummary = { total: units.length, running: 0, done: 0, failed: 0, stopped: 0, finished: 0, toolCalls: 0, longest: null };
  for (const u of units) {
    s[u.status]++;
    s.toolCalls += u.toolCalls ?? 0;
    const t = activeMs(u, now);
    if (!s.longest || t.ms > s.longest.ms) s.longest = { run: u, ms: t.ms, measured: t.measured };
  }
  s.finished = s.done + s.failed + s.stopped;
  return s;
}

/** The subagents and workflow agents of a set of runs (see unitsOf). */
export function summarizeRuns(runs: readonly TimelineRun[], now: Ts): RunSummary {
  return summarize(runs.flatMap(unitsOf), now);
}

// ---------------------------------------------------------------------------
// Stuck agents

export interface StuckRun {
  run: TimelineRun;
  session: Session;
  /** Since the last transcript entry. */
  quietMs: number;
  /** A tool call or a reply was outstanding the whole time. */
  busy: boolean;
}

/**
 * Agents marked running with no activity for AGENT_STUCK_MS (AGENT_STUCK_BUSY_MS when a tool
 * call or a reply is outstanding: a long test run is not stuck). Only machines that report:
 * an offline machine's runs are last known state, and the machine itself needs you.
 */
export function stuckRuns(sessions: readonly Session[], machines: Record<string, Machine>, now: Ts): StuckRun[] {
  const out: StuckRun[] = [];
  for (const s of sessions) {
    const m = machines[s.machineId];
    if (!m || machineLiveness(m, now) !== 'online') continue;
    for (const r of runsOf(s).flatMap(unitsOf)) {
      if (r.status !== 'running') continue;
      const a = usableActivity(r);
      if (!a) continue;
      const quietMs = now - a.lastAt;
      if (quietMs > (a.busy ? AGENT_STUCK_BUSY_MS : AGENT_STUCK_MS)) out.push({ run: r, session: s, quietMs, busy: a.busy });
    }
  }
  return out.sort((x, y) => y.quietMs - x.quietMs);
}
